import * as Sentry from '@sentry/react'

const PII_FIELDS = [
  // Identity
  'ssn',
  'socialsecuritynumber',
  'firstname',
  'lastname',
  'fullname',
  'legalname',
  // Date of birth — covers dateOfBirth, birthdate, birthday, dob
  'dateofbirth',
  'birthdate',
  'birthday',
  'dob',
  // Contact
  'phone',
  'password',
  'email',
  'emailaddress',
  // Address
  'address',
  'streetaddress',
  // Financial — account & routing
  'accountnumber',
  'routingnumber',
  'bankaccount',
  // Payment card
  'cardnumber',
  'cvv',
  'cvc',
  'creditcard',
  'expiry',
  'expirationdate',
  'cardexpiry',
  'exp',
  // Tokens / secrets
  'token',
]

function scrubPii(data) {
  if (typeof data !== 'object' || data === null) return data
  const result = Array.isArray(data) ? [...data] : { ...data }
  for (const key of Object.keys(result)) {
    if (PII_FIELDS.some(field => key.toLowerCase().includes(field))) {
      result[key] = '[Filtered]'
    } else if (typeof result[key] === 'object') {
      result[key] = scrubPii(result[key])
    }
  }
  return result
}

export function initSentry() {
  if (process.env.REACT_APP_SENTRY_ENABLED !== 'true') return

  const dsn = process.env.REACT_APP_SENTRY_DSN
  if (!dsn) {
    return
  }

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV,
    // Performance tracing disabled — error monitoring only
    tracesSampleRate: 0,
    integrations: [
      // Capture only explicit console.error calls — not console.log which is too noisy
      Sentry.consoleLoggingIntegration({ levels: ["error"] }),
    ],
    // Enable logs to be sent to Sentry
    enableLogs: true,
    // Ignore known third-party SDK noise that we cannot fix
    ignoreErrors: [
      // Forte SDK calls JSON.parse on all postMessage events including CRA HMR messages
      '"[object Object]" is not valid JSON',
    ],
    beforeSend(e) {
      if (e.request && e.request.data) {
        e.request.data = scrubPii(e.request.data)
      }
      if (e.extra) {
        e.extra = scrubPii(e.extra)
      }
      return e
    },
    beforeSendLog(log) {
      if (log.message && log.message.includes('MessageRouter ignored message from unknown source')) {
        return null
      }
      return log
    },
  })

  // Suppress the Forte SDK JSON.parse error from the CRA dev overlay.
  // The Forte sandbox SDK listens to all window postMessage events and calls JSON.parse on
  // every message payload, including CRA HMR messages which are plain objects not JSON strings.
  // This is a Forte SDK bug — suppress it to avoid noise in dev. Not an issue in production
  // since HMR does not run there.
  window.addEventListener('error', (event) => {
    if (
      event.message &&
      event.message.includes('"[object Object]" is not valid JSON') &&
      event.filename &&
      event.filename.includes('forte.net')
    ) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }, true)

  // Capture the initial landing page
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: window.location.pathname + window.location.search,
    level: 'info',
    data: { referrer: document.referrer || 'direct' },
  })
}

export { Sentry }
